import React, { useCallback, useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";

import api from "../../../../../services/api";
import { formatDateTime } from "../../../../../utils/formatDate";

import "./Ocorrencias.css";

// Histórico de ocorrências de uma NF. Usado inline no resultado do
// Rastreamento e dentro do ModalOcorrencia (Entregas Pendentes): o fetch e a
// lista ficam aqui para os dois não divergirem.

const finalizerFields = [
  "finalizadora",
  "isFinalizadora",
  "is_finalizadora",
  "ocorrenciaFinalizadora",
  "ocorrencia_finalizadora",
];

const isEnabledValue = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "sim" ||
  value === "S";

const isFinalizingOccurrence = (item) =>
  finalizerFields.some((field) => isEnabledValue(item[field]));

/**
 * Carrega `/tracking/ocorrencias` da NF. `enabled` permite ao modal só buscar
 * quando abre. `ocorrencias` é null enquanto carrega.
 */
export function useOcorrencias(idNf, enabled = true) {
  const [ocorrencias, setOcorrencias] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;

    async function load() {
      if (!idNf) {
        setOcorrencias([]);
        return;
      }

      setOcorrencias(null);
      setError(null);

      try {
        const result = await api.get("/tracking/ocorrencias", {
          params: { idNf },
        });

        if (cancelled) return;

        if (result.status === 200) {
          setOcorrencias(
            result.data.map((item) => ({
              data_criacao_f: formatDateTime(item.data_criacao),
              ...item,
            }))
          );
        }
      } catch (_) {
        if (!cancelled) {
          setError("Não foi possível carregar o histórico desta entrega.");
          setOcorrencias([]);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [idNf, enabled]);

  return { ocorrencias, error };
}

/**
 * Lista das ocorrências com os estados de carregando / erro / vazio.
 * `emptyMessage` troca o texto do estado vazio (o resultado explica o que
 * falta; o modal usa o texto curto padrão).
 */
export function ListaOcorrencias({
  ocorrencias,
  error,
  emptyMessage = "Nenhuma ocorrência registrada para esta entrega.",
  compact = false,
}) {
  const handleOpenImage = useCallback((urlImage) => {
    if (urlImage) {
      window.open(urlImage, "_blank", "noopener,noreferrer");
    }
  }, []);

  const stateClass = `tracking-modal-state${compact ? " tracking-modal-state--compact" : ""}`;

  if (ocorrencias === null && !error) {
    return (
      <div className={stateClass}>
        <Spinner color="info" size={compact ? "sm" : undefined} />
        <span>Carregando ocorrências...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${stateClass} tracking-modal-state--error`}>
        <i className="tim-icons icon-alert-circle-exc" />
        <span>{error}</span>
      </div>
    );
  }

  if (ocorrencias.length === 0) {
    return (
      <div className={stateClass}>
        <i className="tim-icons icon-time-alarm" />
        <span>{emptyMessage}</span>
      </div>
    );
  }

  return (
    <div className="tracking-events-list">
      {ocorrencias.map((item, index) => {
        const isFinalizer = isFinalizingOccurrence(item);
        const markerLabel = isFinalizer
          ? "Finalização da entrega"
          : `Ocorrência ${index + 1}`;

        return (
          <div
            key={`${item.codigo || "ocorrencia"}-${index}`}
            className={`tracking-event-item${
              isFinalizer ? " tracking-event-item--finalizer" : ""
            }`}
          >
            <div className="tracking-event-marker">
              <span aria-label={markerLabel} title={markerLabel}>
                {isFinalizer ? (
                  <i className="tim-icons icon-delivery-fast" aria-hidden="true" />
                ) : (
                  index + 1
                )}
              </span>
            </div>
            <div className="tracking-event-content">
              <div className="tracking-event-header">
                <strong>{item.nome || "Ocorrência"}</strong>
                <span>{item.data_criacao_f || "-"}</span>
              </div>
              <p>{item.descricao || "Sem descrição registrada."}</p>
              <div className="tracking-event-footer">
                <div className="tracking-event-meta">
                  <span>Código {item.codigo || "-"}</span>
                  {isFinalizer ? (
                    <span className="tracking-event-finalizer-badge">
                      <i className="tim-icons icon-delivery-fast" aria-hidden="true" />
                      Finalização da entrega
                    </span>
                  ) : null}
                </div>
                {item.url_image ? (
                  <Button
                    className="tracking-link-button"
                    type="button"
                    onClick={() => handleOpenImage(item.url_image)}
                  >
                    <i className="tim-icons icon-attach-87" /> Anexo
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
